import { create } from 'zustand';
import {
  EVENT_CATEGORY_KEYS,
  EVENT_FILTER_PRESETS,
  type EventCategory,
  type EventFilterPreset,
} from '@/pages/workflow/component/controls/helpers/panel-event-helpers';

const STORAGE_KEY = 'workflow-events-filter-v1';

type EnabledMap = Record<EventCategory, boolean>;

const buildAllEnabled = (): EnabledMap =>
  EVENT_CATEGORY_KEYS.reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {} as EnabledMap);

function readStored(): EnabledMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return buildAllEnabled();
    const parsed = JSON.parse(raw) as Partial<Record<EventCategory, boolean>>;
    return EVENT_CATEGORY_KEYS.reduce((acc, key) => {
      acc[key] = typeof parsed[key] === 'boolean' ? parsed[key]! : true;
      return acc;
    }, {} as EnabledMap);
  } catch {
    return buildAllEnabled();
  }
}

function persist(enabled: EnabledMap) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(enabled));
  } catch {
    // ignore
  }
}

interface EventsFilterState {
  enabled: EnabledMap;
  toggleCategory: (category: EventCategory) => void;
  applyPreset: (preset: EventFilterPreset) => void;
}

export const useEventsFilterStore = create<EventsFilterState>((set) => ({
  enabled: readStored(),
  toggleCategory: (category) =>
    set((state) => {
      const enabled = {
        ...state.enabled,
        [category]: !state.enabled[category],
      };
      persist(enabled);
      return { enabled };
    }),
  applyPreset: (preset) =>
    set(() => {
      const presetDef = EVENT_FILTER_PRESETS.find((p) => p.key === preset);
      const active = new Set(presetDef?.categories ?? EVENT_CATEGORY_KEYS);
      const enabled = EVENT_CATEGORY_KEYS.reduce((acc, key) => {
        acc[key] = active.has(key);
        return acc;
      }, {} as EnabledMap);
      persist(enabled);
      return { enabled };
    }),
}));
